import React from 'react';
import { Box } from '@mui/material';
import {
  timeToDbFormat,
} from '@features/settings/utils/businessHoursUtils';
import TimelineHeader from './TimelineHeader';
import TimelineDayRow from './TimelineDayRow';
import type { DayHours, DayOverride } from './ScheduleEditor.types';

// Sunday-first ordering
const WEEK_ORDER = [0, 1, 2, 3, 4, 5, 6];

interface WeeklyTimelineProps {
  dayHours: DayHours[];
  overrideMap: Map<number, DayOverride>;
  timezone: string;
  closedLabel: string;
  onDayClick?: (dayIndex: number) => void;
  pendingDays?: Set<number>;
}

/**
 * Computes the visible hour range for the timeline.
 * Default: 7am-7pm, auto-expands if blocks fall outside with 1hr padding.
 */
const computeTimeRange = (
  dayHours: DayHours[],
  overrideMap: Map<number, DayOverride>,
  timezone: string
): { startHour: number; endHour: number } => {
  let minHour = 7;
  let maxHour = 19;

  for (const day of dayHours) {
    if (day.isClosed) continue;
    for (const block of day.timeBlocks) {
      const openStr = block.openTime ? timeToDbFormat(block.openTime, timezone) : null;
      const closeStr = block.closedTime ? timeToDbFormat(block.closedTime, timezone) : null;

      if (openStr) {
        const [hh] = openStr.split(':').map(Number);
        if (hh < minHour) minHour = Math.max(0, hh - 1);
      }
      if (closeStr) {
        const [hh, mm] = closeStr.split(':').map(Number);
        const endH = mm > 0 ? hh + 1 : hh;
        if (endH > maxHour) maxHour = Math.min(24, endH + 1);
      }
    }
  }

  // Also check overrides
  for (const override of overrideMap.values()) {
    if (override.isAvailable && override.openTime && override.closedTime) {
      const [oh] = override.openTime.split(':').map(Number);
      const [ch, cm] = override.closedTime.split(':').map(Number);
      if (oh < minHour) minHour = Math.max(0, oh - 1);
      const endH = cm > 0 ? ch + 1 : ch;
      if (endH > maxHour) maxHour = Math.min(24, endH + 1);
    }
  }

  return { startHour: minHour, endHour: maxHour };
};

const WeeklyTimeline: React.FC<WeeklyTimelineProps> = ({
  dayHours,
  overrideMap,
  timezone,
  closedLabel,
  onDayClick,
  pendingDays,
}) => {
  const { startHour, endHour } = computeTimeRange(dayHours, overrideMap, timezone);

  return (
    <Box>
      <TimelineHeader startHour={startHour} endHour={endHour} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {WEEK_ORDER.map((dayIndex) => {
          const day = dayHours.find((d) => d.dayOfWeek === dayIndex);
          if (!day) return null;

          return (
            <TimelineDayRow
              key={dayIndex}
              dayIndex={dayIndex}
              dayHours={day}
              override={overrideMap.get(dayIndex)}
              startHour={startHour}
              endHour={endHour}
              timezone={timezone}
              closedLabel={closedLabel}
              onClick={onDayClick}
              hasPendingChanges={pendingDays?.has(dayIndex)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default WeeklyTimeline;
